import React, { useState, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, Check } from 'lucide-react';
import { AuthContext } from '../../context/AuthContext';
import { ToastContext } from '../../context/ToastContext';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Accordion from '../../components/common/Accordion';
import MainLayout from '../../components/layout/MainLayout';
import { fetchUsers } from '../../utils/api';

// 약관 요약(인라인 펼침용) — 전체 본문은 법적 페이지 링크로 연결
const TERMS_SUMMARY = `제1조 (목적) 본 약관은 회사가 제공하는 장례 서비스 이용과 관련하여 회사와 이용자 간의 권리·의무 및 책임사항을 규정합니다.
제4조 (회원가입) 이용자는 가입 양식에 따라 정보를 기입하고 본 약관에 동의함으로써 회원가입을 신청합니다.
제7조 (회원탈퇴) 회원은 언제든지 탈퇴를 요청할 수 있으며 회사는 즉시 처리합니다.`;

const PRIVACY_SUMMARY = `수집 항목: 이름, 이메일, 전화번호(필수) / 배송지 주소(선택)
이용 목적: 회원 식별·인증, 부고 등록 및 화환 주문·배송, 고객 지원
보유 기간: 회원 탈퇴 시까지(관계 법령에 따라 일정 기간 보관될 수 있음)`;

// 비밀번호 강도 계산 (0~4)
const getPasswordStrength = (pw) => {
  if (!pw) return 0;
  let score = 0;
  if (pw.length >= 6) score += 1;
  if (pw.length >= 10) score += 1;
  if (/[A-Z]/.test(pw) || /[a-z]/.test(pw)) score += 1;
  if (/\d/.test(pw)) score += 1;
  if (/[^A-Za-z0-9]/.test(pw)) score += 1;
  return Math.min(score, 4);
};

const STRENGTH_META = [
  { label: '매우 약함', color: 'bg-red-500', text: 'text-red-600' },
  { label: '약함', color: 'bg-orange-500', text: 'text-orange-600' },
  { label: '보통', color: 'bg-yellow-500', text: 'text-yellow-600' },
  { label: '강함', color: 'bg-lime-500', text: 'text-lime-600' },
  { label: '매우 강함', color: 'bg-green-600', text: 'text-green-700' },
];

const SignupPage = () => {
  const navigate = useNavigate();
  const { signup } = useContext(AuthContext);
  const { showToast } = useContext(ToastContext);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: ''
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // 이메일 중복 확인 상태: idle | checking | available | taken
  const [emailCheck, setEmailCheck] = useState('idle');

  // 가입 성공 후 환영 안내 단계
  const [signupSuccess, setSignupSuccess] = useState(false);

  // 약관 동의 상태
  const [agreements, setAgreements] = useState({
    terms: false,
    privacy: false,
    marketing: false, // 선택
  });

  const allRequiredAgreed = agreements.terms && agreements.privacy;
  const allAgreed = agreements.terms && agreements.privacy && agreements.marketing;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    // 이메일이 변경되면 중복 확인 결과 초기화 (재확인 필요)
    if (name === 'email') {
      setEmailCheck('idle');
    }
  };

  // 이메일 중복 확인 (mock: users.json 조회)
  const handleEmailCheck = async () => {
    const email = formData.email.trim();
    if (!email) {
      setErrors(prev => ({ ...prev, email: '이메일을 입력해주세요.' }));
      return;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      setErrors(prev => ({ ...prev, email: '올바른 이메일 형식이 아닙니다.' }));
      return;
    }

    setEmailCheck('checking');
    try {
      const users = await fetchUsers();
      const taken = users.some(
        (u) => u.email && u.email.toLowerCase() === email.toLowerCase()
      );
      if (taken) {
        setEmailCheck('taken');
        setErrors(prev => ({ ...prev, email: '이미 사용 중인 이메일입니다.' }));
        showToast('이미 사용 중인 이메일입니다.', 'error');
      } else {
        setEmailCheck('available');
        setErrors(prev => ({ ...prev, email: '' }));
        showToast('사용 가능한 이메일입니다.', 'success');
      }
    } catch (error) {
      setEmailCheck('idle');
      showToast('이메일 확인 중 오류가 발생했습니다.', 'error');
    }
  };

  // 단일 필드 검증 (onBlur 실시간 검증용)
  const validateField = (name, value = formData[name]) => {
    let message = '';
    switch (name) {
      case 'name':
        if (!value.trim()) message = '이름을 입력해주세요.';
        break;
      case 'email':
        if (!value) message = '이메일을 입력해주세요.';
        else if (!/\S+@\S+\.\S+/.test(value)) message = '올바른 이메일 형식이 아닙니다.';
        break;
      case 'password':
        if (!value) message = '비밀번호를 입력해주세요.';
        else if (value.length < 6) message = '비밀번호는 최소 6자 이상이어야 합니다.';
        break;
      case 'confirmPassword':
        if (!value) message = '비밀번호 확인을 입력해주세요.';
        else if (formData.password !== value) message = '비밀번호가 일치하지 않습니다.';
        break;
      case 'phone':
        if (!value) message = '전화번호를 입력해주세요.';
        else if (!/^01[0-9]-\d{3,4}-\d{4}$/.test(value)) message = '올바른 전화번호 형식이 아닙니다. (예: 010-1234-5678)';
        break;
      default:
        break;
    }
    setErrors(prev => ({ ...prev, [name]: message }));
    return !message;
  };

  const handleBlur = (e) => {
    validateField(e.target.name);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = '이름을 입력해주세요.';
    }

    if (!formData.email) {
      newErrors.email = '이메일을 입력해주세요.';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = '올바른 이메일 형식이 아닙니다.';
    } else if (emailCheck === 'taken') {
      newErrors.email = '이미 사용 중인 이메일입니다.';
    } else if (emailCheck !== 'available') {
      newErrors.email = '이메일 중복 확인을 해주세요.';
    }

    if (!formData.password) {
      newErrors.password = '비밀번호를 입력해주세요.';
    } else if (formData.password.length < 6) {
      newErrors.password = '비밀번호는 최소 6자 이상이어야 합니다.';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = '비밀번호 확인을 입력해주세요.';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = '비밀번호가 일치하지 않습니다.';
    }

    if (!formData.phone) {
      newErrors.phone = '전화번호를 입력해주세요.';
    } else if (!/^01[0-9]-\d{3,4}-\d{4}$/.test(formData.phone)) {
      newErrors.phone = '올바른 전화번호 형식이 아닙니다. (예: 010-1234-5678)';
    }

    if (!agreements.terms) {
      newErrors.terms = '이용약관에 동의해주세요.';
    }

    if (!agreements.privacy) {
      newErrors.privacy = '개인정보취급방침에 동의해주세요.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showToast('입력 정보를 확인해주세요.', 'error');
      return;
    }

    setLoading(true);

    try {
      const result = await signup({ ...formData, marketingConsent: agreements.marketing });

      if (result.success) {
        showToast('회원가입이 완료되었습니다.', 'success');
        // 즉시 이동 대신 환영/안내 단계 노출
        setSignupSuccess(true);
      } else {
        showToast(result.error || '회원가입에 실패했습니다.', 'error');
      }
    } catch (error) {
      showToast('회원가입 중 오류가 발생했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const formatPhoneNumber = (value) => {
    const numbers = value.replace(/[^\d]/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 7) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setFormData(prev => ({
      ...prev,
      phone: formatted
    }));
    if (errors.phone) {
      setErrors(prev => ({
        ...prev,
        phone: ''
      }));
    }
  };

  const toggleAll = (checked) => {
    setAgreements({ terms: checked, privacy: checked, marketing: checked });
    if (checked) {
      setErrors(prev => ({ ...prev, terms: '', privacy: '' }));
    }
  };

  const toggleAgreement = (key, checked) => {
    setAgreements(prev => ({ ...prev, [key]: checked }));
    if (checked && errors[key]) {
      setErrors(prev => ({ ...prev, [key]: '' }));
    }
  };

  const strength = getPasswordStrength(formData.password);
  const strengthMeta = STRENGTH_META[Math.max(0, strength - 1)] || STRENGTH_META[0];

  // 가입 성공 후 환영/안내 단계
  if (signupSuccess) {
    return (
      <MainLayout>
        <div className="min-h-[calc(100vh-200px)] bg-secondary-100 flex items-center justify-center px-4 py-12">
          <div className="max-w-md w-full">
            <div className="bg-white rounded-lg shadow-md p-8 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <Check size={32} className="text-green-600" aria-hidden="true" />
              </div>
              <h1 className="text-2xl font-bold text-primary mb-2">
                {formData.name.trim() ? `${formData.name.trim()}님, 환영합니다!` : '환영합니다!'}
              </h1>
              <p className="text-accent-600 mb-6">
                회원가입이 완료되었습니다. 이제 부고 작성과 화환 주문 서비스를
                이용하실 수 있습니다.
              </p>
              <div className="mb-6 rounded-lg border border-accent-200 bg-secondary-50 p-4 text-left text-sm text-accent-700">
                <p className="font-medium text-primary mb-2">가입 정보</p>
                <p>이메일: {formData.email}</p>
                <p className="mt-1 text-xs text-accent-500">
                  ※ 본 사이트는 목업으로, 실제 인증 메일은 발송되지 않습니다.
                </p>
              </div>
              <div className="space-y-3">
                <Button
                  type="button"
                  variant="primary"
                  fullWidth
                  onClick={() => navigate('/')}
                >
                  서비스 시작하기
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  fullWidth
                  onClick={() => navigate('/obituaries/create')}
                >
                  부고 작성하러 가기
                </Button>
              </div>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="min-h-[calc(100vh-200px)] bg-secondary-100 flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full">
        {/* 로고 및 타이틀 */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-primary mb-2">
            회원가입
          </h1>
          <p className="text-accent-600">
            장례 서비스 이용을 위한 정보를 입력해주세요.
          </p>
        </div>

        {/* 회원가입 폼 */}
        <div className="bg-white rounded-lg shadow-md p-8">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Input 공통 컴포넌트가 onBlur 를 전달하지 않으므로 래퍼 div 의 onBlur(버블링)로 실시간 검증 */}
            <div onBlur={handleBlur}>
              <Input
                type="text"
                name="name"
                label="이름"
                value={formData.name}
                onChange={handleChange}
                placeholder="홍길동"
                error={errors.name}
                required
              />
            </div>

            {/* 이메일 + 중복 확인 */}
            <div className="w-full">
              <label htmlFor="email" className="block text-sm font-medium text-primary mb-1">
                이메일<span className="text-red-600 ml-1">*</span>
              </label>
              <div className="flex gap-2">
                <input
                  type="email"
                  name="email"
                  id="email"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="example@email.com"
                  required
                  aria-invalid={!!errors.email}
                  className={`
                    flex-1 px-4 py-2 rounded-lg border
                    ${errors.email ? 'border-red-500 focus:ring-red-500' : 'border-accent-300 focus:ring-primary-500'}
                    bg-white text-primary placeholder-accent-400
                    focus:outline-none focus:ring-2 focus:border-transparent
                    transition-all duration-200
                  `}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleEmailCheck}
                  disabled={emailCheck === 'checking'}
                  className="shrink-0 whitespace-nowrap"
                >
                  {emailCheck === 'checking' ? '확인 중...' : '중복 확인'}
                </Button>
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
              )}
              {!errors.email && emailCheck === 'available' && (
                <p className="mt-1 flex items-center gap-1 text-sm text-green-600">
                  <Check size={14} aria-hidden="true" />
                  사용 가능한 이메일입니다.
                </p>
              )}
            </div>

            {/* 비밀번호 + 표시 토글 + 강도 표시 */}
            <div className="w-full">
              <label htmlFor="password" className="block text-sm font-medium text-primary mb-1">
                비밀번호<span className="text-red-600 ml-1">*</span>
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  id="password"
                  value={formData.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="최소 6자 이상"
                  required
                  className={`
                    w-full px-4 py-2 pr-11 rounded-lg border
                    ${errors.password ? 'border-red-500 focus:ring-red-500' : 'border-accent-300 focus:ring-primary-500'}
                    bg-white text-primary placeholder-accent-400
                    focus:outline-none focus:ring-2 focus:border-transparent
                    transition-all duration-200
                  `}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? '비밀번호 숨기기' : '비밀번호 표시'}
                  aria-pressed={showPassword}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-accent-500 hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded-r-lg"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {/* 강도 표시기 */}
              {formData.password && (
                <div className="mt-2">
                  <div className="flex gap-1">
                    {[0, 1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className={`h-1.5 flex-1 rounded-full ${
                          i < strength ? strengthMeta.color : 'bg-accent-200'
                        }`}
                      />
                    ))}
                  </div>
                  <p className={`mt-1 text-xs font-medium ${strengthMeta.text}`}>
                    비밀번호 강도: {strengthMeta.label}
                  </p>
                </div>
              )}
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password}</p>
              )}
            </div>

            {/* 비밀번호 확인 + 표시 토글 */}
            <div className="w-full">
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-primary mb-1">
                비밀번호 확인<span className="text-red-600 ml-1">*</span>
              </label>
              <div className="relative">
                <input
                  type={showConfirm ? 'text' : 'password'}
                  name="confirmPassword"
                  id="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="비밀번호를 다시 입력하세요"
                  required
                  className={`
                    w-full px-4 py-2 pr-11 rounded-lg border
                    ${errors.confirmPassword ? 'border-red-500 focus:ring-red-500' : 'border-accent-300 focus:ring-primary-500'}
                    bg-white text-primary placeholder-accent-400
                    focus:outline-none focus:ring-2 focus:border-transparent
                    transition-all duration-200
                  `}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm((v) => !v)}
                  aria-label={showConfirm ? '비밀번호 숨기기' : '비밀번호 표시'}
                  aria-pressed={showConfirm}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-accent-500 hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded-r-lg"
                >
                  {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
              )}
            </div>

            <div onBlur={handleBlur}>
              <Input
                type="tel"
                name="phone"
                label="전화번호"
                value={formData.phone}
                onChange={handlePhoneChange}
                placeholder="010-1234-5678"
                error={errors.phone}
                required
              />
            </div>

            {/* 약관 동의 */}
            <div className="space-y-3 pt-4">
              {/* 전체 동의 */}
              <label className="flex items-center gap-2 rounded-lg border border-accent-300 bg-secondary-50 px-3 py-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={allAgreed}
                  onChange={(e) => toggleAll(e.target.checked)}
                  className="h-4 w-4 rounded border-accent-300 text-primary focus:ring-primary-500"
                />
                <span className="text-sm font-semibold text-primary">
                  전체 동의 (선택 항목 포함)
                </span>
              </label>

              <div className="space-y-2 pl-1">
                <div className="flex items-start">
                  <input
                    type="checkbox"
                    id="agreeTerms"
                    checked={agreements.terms}
                    onChange={(e) => toggleAgreement('terms', e.target.checked)}
                    className="mt-1 h-4 w-4 text-primary border-accent-300 rounded focus:ring-primary-500"
                  />
                  <label htmlFor="agreeTerms" className="ml-2 text-sm text-primary">
                    <span className="text-primary-600">[필수]</span>{' '}
                    <Link to="/legal/terms" className="text-primary hover:underline">
                      이용약관
                    </Link>
                    에 동의합니다.
                  </label>
                </div>
                {errors.terms && (
                  <p className="text-sm text-red-600 ml-6">{errors.terms}</p>
                )}

                <div className="flex items-start">
                  <input
                    type="checkbox"
                    id="agreePrivacy"
                    checked={agreements.privacy}
                    onChange={(e) => toggleAgreement('privacy', e.target.checked)}
                    className="mt-1 h-4 w-4 text-primary border-accent-300 rounded focus:ring-primary-500"
                  />
                  <label htmlFor="agreePrivacy" className="ml-2 text-sm text-primary">
                    <span className="text-primary-600">[필수]</span>{' '}
                    <Link to="/legal/privacy" className="text-primary hover:underline">
                      개인정보취급방침
                    </Link>
                    에 동의합니다.
                  </label>
                </div>
                {errors.privacy && (
                  <p className="text-sm text-red-600 ml-6">{errors.privacy}</p>
                )}

                <div className="flex items-start">
                  <input
                    type="checkbox"
                    id="agreeMarketing"
                    checked={agreements.marketing}
                    onChange={(e) => toggleAgreement('marketing', e.target.checked)}
                    className="mt-1 h-4 w-4 text-primary border-accent-300 rounded focus:ring-primary-500"
                  />
                  <label htmlFor="agreeMarketing" className="ml-2 text-sm text-accent-700">
                    <span className="text-accent-500">[선택]</span>{' '}
                    마케팅 정보 수신(이메일/SMS)에 동의합니다.
                  </label>
                </div>
              </div>

              {/* 약관 인라인 펼침(아코디언) — 전체 본문은 법적 페이지에서 확인 */}
              <div className="space-y-2 pt-1">
                <Accordion title="이용약관 주요 내용 보기">
                  <p className="whitespace-pre-line text-sm text-accent-700">
                    {TERMS_SUMMARY}
                  </p>
                  <Link
                    to="/legal/terms"
                    className="mt-2 inline-block text-sm font-medium text-primary hover:underline"
                  >
                    이용약관 전문 보기 →
                  </Link>
                </Accordion>
                <Accordion title="개인정보 수집·이용 주요 내용 보기">
                  <p className="whitespace-pre-line text-sm text-accent-700">
                    {PRIVACY_SUMMARY}
                  </p>
                  <Link
                    to="/legal/privacy"
                    className="mt-2 inline-block text-sm font-medium text-primary hover:underline"
                  >
                    개인정보취급방침 전문 보기 →
                  </Link>
                </Accordion>
              </div>

              {!allRequiredAgreed && (
                <p className="text-xs text-accent-500 ml-1">
                  필수 항목에 모두 동의해야 가입이 완료됩니다.
                </p>
              )}
            </div>

            <Button
              type="submit"
              variant="primary"
              fullWidth
              disabled={loading}
              className="mt-6"
            >
              {loading ? '가입 중...' : '회원가입'}
            </Button>
          </form>

          {/* 로그인 링크 */}
          <div className="mt-6 text-center">
            <p className="text-sm text-accent-600">
              이미 계정이 있으신가요?{' '}
              <Link
                to="/login"
                className="text-primary font-medium hover:text-primary-700 transition-colors"
              >
                로그인
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
    </MainLayout>
  );
};

export default SignupPage;
